import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import api, { studentsAPI, tutorsAPI, parentsAPI } from '../utils/api';
import './UniversalSearch.css';

export default function UniversalSearch() {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const safeT = (key, fallback) => {
    const val = t(key);
    return val !== key ? val : fallback;
  };

  const handleSearch = async (e, forceQuery) => {
    if (e) e.preventDefault();
    if (loading) return;
    const q = forceQuery || query;
    if (!q || !q.trim()) return;
    
    setQuery(q);
    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const res = await api.get(`/admin/universal-search/${encodeURIComponent(q.trim())}`);
      setResult(res.data);
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError(safeT('universalSearch.notFound', 'User not found'));
      }
    } finally {
      setLoading(false);
    }
  };

  const getLangName = (nameObj) => {
    if (!nameObj) return '';
    if (typeof nameObj === 'string') return nameObj;
    return i18n.language === 'zh' ? (nameObj.zh || nameObj.en) : (nameObj.en || nameObj.zh);
  };

  const handleDelete = async () => {
    if (!result) return;
    if (!window.confirm(`Are you sure you want to permanently delete this ${result.role}?`)) return;
    try {
      if (result.role === 'student') await studentsAPI.delete(result.data.id);
      else if (result.role === 'tutor') await tutorsAPI.delete(result.data.id);
      else if (result.role === 'parent') await parentsAPI.delete(result.data.id);
      alert(`${result.role} deleted successfully.`);
      setResult(null);
      setQuery('');
    } catch {
      alert(`Failed to delete ${result.role}.`);
    }
  };

  const handleToggleStatus = async () => {
    if (!result) return;

    // ✅ Normalize status — handle 'active'/'Active'/'inactive'/'Inactive'/'unknown' correctly
    const currentStatus = String(result.data.status || '').toLowerCase();
    const isCurrentlyActive = currentStatus === 'active';
    const newStatus = isCurrentlyActive ? 'inactive' : 'active';

    setStatusLoading(true);
    try {
      if (result.role === 'student') await studentsAPI.toggleStatus(result.data.id, newStatus);
      else if (result.role === 'tutor') await tutorsAPI.toggleStatus(result.data.id, newStatus);
      else if (result.role === 'parent') await parentsAPI.toggleStatus(result.data.id, newStatus);

      // ✅ Always re-fetch fresh data from server after toggle
      // This ensures UI shows real DB state (not stale local state)
      await handleSearch(null, query);
    } catch (err) {
      // ✅ Show actual error message for easier debugging
      const msg = err?.response?.data?.message || err?.message || 'Unknown error';
      alert(`Failed to change status: ${msg}`);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleDownloadPDF = () => {
    if (!result) return;
    const printWindow = window.open('', '_blank');
    const roleName = result.role.charAt(0).toUpperCase() + result.role.slice(1);
    const profileName = getLangName(result.data.name);
    const pendingMonths = result.pendingMonths || [];

    let html = `
      <html>
        <head>
          <title>${roleName} Profile - ${profileName}</title>
          <style>
            body { font-family: 'Arial', sans-serif; padding: 40px; color: #333; line-height: 1.6; }
            h1 { color: #7b1d3a; border-bottom: 2px solid #eee; padding-bottom: 10px; }
            h2 { color: #555; margin-top: 30px; font-size: 1.2rem; }
            h3 { color: #7b1d3a; margin-top: 20px; font-size: 1.05rem; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; font-size: 14px; }
            th { background-color: #f8f9fa; }
            .info-grid { display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 20px; }
            .info-item { min-width: 200px; }
            .label { font-size: 12px; color: #777; text-transform: uppercase; font-weight: 600; margin-bottom: 4px; }
            .value { font-size: 16px; font-weight: bold; }
            .outstanding-box { background: #fff5f5; border: 1px solid #ffcccc; border-radius: 8px; padding: 16px; margin-top: 20px; }
            .outstanding-title { color: #c62828; font-weight: bold; margin-bottom: 12px; }
            .pending-row { background: #fff5f5; color: #c62828; }
          </style>
        </head>
        <body>
          <h1>${roleName} Profile: ${profileName}</h1>
          <div class="info-grid">
            <div class="info-item"><div class="label">ID</div><div class="value">${result.data.studentId || 'N/A'}</div></div>
            <div class="info-item"><div class="label">Email</div><div class="value">${result.data.email || 'N/A'}</div></div>
            <div class="info-item"><div class="label">Mobile</div><div class="value">${result.data.mobile || 'N/A'}</div></div>
            <div class="info-item"><div class="label">Status</div><div class="value">${result.data.status || 'N/A'}</div></div>
    `;

    if (result.role === 'student') {
      html += `
            <div class="info-item"><div class="label">Level</div><div class="value">${result.data.currentLevel || 'N/A'}</div></div>
            <div class="info-item"><div class="label">Fee Status</div><div class="value">${result.data.feeStatus || 'N/A'}</div></div>
      `;
    }
    html += `</div>`;

    if (pendingMonths.length > 0) {
      const grouped = {};
      pendingMonths.forEach(pm => {
        const name = getLangName(pm.batchName) || 'Unknown Batch';
        if (!grouped[name]) grouped[name] = [];
        grouped[name].push(pm);
      });
      html += `<h2>⚠ Outstanding Fees (${pendingMonths.length} month${pendingMonths.length === 1 ? '' : 's'})</h2>`;
      Object.entries(grouped).forEach(([batchName, months]) => {
        html += `
          <h3>${batchName}</h3>
          <table>
            <tr><th>Month</th><th>Expected Amount</th><th>Status</th></tr>
            ${months.map(pm => `
              <tr class="pending-row">
                <td>${pm.month}</td>
                <td>${pm.expectedAmount ? '$' + pm.expectedAmount + ' HKD' : 'N/A'}</td>
                <td><strong>UNPAID</strong></td>
              </tr>
            `).join('')}
          </table>
        `;
      });
    }

    if (result.role === 'student' && result.parent) {
      html += `
        <h2>Parent Information</h2>
        <div class="info-grid">
          <div class="info-item"><div class="label">Name</div><div class="value">${getLangName(result.parent.name)}</div></div>
          <div class="info-item"><div class="label">Status</div><div class="value">${result.parent.status}</div></div>
          <div class="info-item"><div class="label">Email</div><div class="value">${result.parent.email || 'N/A'}</div></div>
          <div class="info-item"><div class="label">Mobile</div><div class="value">${result.parent.mobile || 'N/A'}</div></div>
        </div>
      `;
    }

    if (result.batches?.length > 0) {
      html += `
        <h2>Batches</h2>
        <table>
          <tr><th>Batch Name</th><th>Schedule</th><th>Fee</th></tr>
          ${result.batches.map(b => `
            <tr>
              <td>${getLangName(b.name)}</td>
              <td>${b.days?.join(', ')} @ ${b.time}</td>
              <td>${b.feeAmount ? '$' + b.feeAmount + ' HKD' : 'N/A'}</td>
            </tr>
          `).join('')}
        </table>
      `;
    }

    if (result.payments?.length > 0) {
      const grouped = {};
      result.payments.forEach(p => {
        const batchName = p.batch ? getLangName(p.batch.name) : 'General / Other';
        if (!grouped[batchName]) grouped[batchName] = [];
        grouped[batchName].push(p);
      });
      html += `<h2>Payment History</h2>`;
      Object.entries(grouped).forEach(([batchName, payments]) => {
        html += `
          <h3>${batchName}</h3>
          <table>
            <tr><th>Date</th><th>Month</th><th>Method</th><th>Amount</th><th>Status</th></tr>
            ${payments.map(p => `
              <tr>
                <td>${new Date(p.createdAt || Date.now()).toLocaleDateString()}</td>
                <td>${p.month}</td>
                <td>${p.method}</td>
                <td>$${p.amount}</td>
                <td>${p.verificationStatus}</td>
              </tr>
            `).join('')}
          </table>
        `;
      });
    }

    html += `
        <script>
          window.onload = function() { 
            setTimeout(() => { window.print(); window.close(); }, 500);
          }
        </script>
        </body>
      </html>
    `;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  // ✅ Normalize to lowercase — handles 'active','Active','inactive','Inactive','unknown' all correctly
  const isActive = result?.data && String(result.data.status || '').toLowerCase() === 'active';
  const pendingMonths = result?.pendingMonths || [];

  // Group pending months by batch name
  const groupedPending = {};
  pendingMonths.forEach(pm => {
    const name = getLangName(pm.batchName) || 'Unknown Batch';
    if (!groupedPending[name]) groupedPending[name] = [];
    groupedPending[name].push(pm);
  });

  // Group paid payments by batch
  const groupedPayments = {};
  (result?.payments || []).forEach(p => {
    const batchName = p.batch ? getLangName(p.batch.name) : 'General / Other';
    if (!groupedPayments[batchName]) groupedPayments[batchName] = [];
    groupedPayments[batchName].push(p);
  });

  return (
    <Layout title={safeT('nav.universalSearch', 'Universal User Search')}>
      <div className="us-container">

        {/* Header */}
        <div className="us-header">
          <h1 className="us-title">{safeT('universalSearch.title', 'Universal User Search')}</h1>
          <p className="us-subtitle">{safeT('universalSearch.subtitle', 'Search for Students, Tutors, or Parents by ID, Email, Name, or Mobile')}</p>
        </div>

        {/* Search Box */}
        <div className="us-search-box">
          <form onSubmit={handleSearch} className="us-search-form">
            <div className="us-search-input-wrapper">
              <input
                type="text"
                className="us-search-input"
                placeholder={safeT('universalSearch.placeholder', 'Enter ID, Email, or Mobile...')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <button type="submit" className="us-search-btn" disabled={loading}>
              {loading ? safeT('common.loading', 'Loading...') : safeT('universalSearch.searchBtn', 'Search')}
            </button>
          </form>
          {error && <p style={{ color: '#c62828', marginTop: '16px', fontSize: '1rem', fontWeight: 500, textAlign: 'center' }}>{error}</p>}
        </div>

        {/* Results */}
        {result && (
          <div className="us-result-container">
            <div className="us-cards-grid">

              {/* ── Profile Card ── */}
              <div className="us-card" style={{ gridColumn: '1 / -1' }}>
                <div className="us-card-header">
                  <h3 className="us-card-title">
                    {result.role.charAt(0).toUpperCase() + result.role.slice(1)} Information
                  </h3>
                  <div className="us-actions" style={{ marginTop: 0 }}>
                    <button onClick={handleDownloadPDF} className="us-btn-outline" style={{ borderColor: 'var(--wine)', color: 'var(--wine)' }}>
                      📄 Download PDF
                    </button>
                    <button onClick={handleToggleStatus} className="us-btn-outline"
                      disabled={statusLoading}
                      style={{ borderColor: isActive ? '#c62828' : '#2e7d32', color: isActive ? '#c62828' : '#2e7d32', opacity: statusLoading ? 0.6 : 1 }}>
                      {statusLoading ? '⏳ Updating...' : (isActive ? 'Deactivate' : 'Activate')}
                    </button>
                    <button onClick={handleDelete} className="us-btn-danger">Delete</button>
                  </div>
                </div>

                <div className="us-profile-header">
                  <div className="us-avatar">
                    {result.data.photoUrl ? <img src={result.data.photoUrl} alt="Profile" /> : '👤'}
                  </div>
                  <div>
                    <h4 className="us-profile-name">{getLangName(result.data.name)}</h4>
                    {result.role === 'student' && <div className="us-profile-id">{result.data.studentId}</div>}
                  </div>
                </div>

                <div className="us-info-grid">
                  <div className="us-info-item">
                    <span className="us-info-label">Email</span>
                    <span className="us-info-value">{result.data.email || 'N/A'}</span>
                  </div>
                  <div className="us-info-item">
                    <span className="us-info-label">Mobile</span>
                    <span className="us-info-value">{result.data.mobile || 'N/A'}</span>
                  </div>
                  <div className="us-info-item">
                    <span className="us-info-label">Status</span>
                    <span className={`us-badge ${isActive ? 'us-badge-active' : 'us-badge-inactive'}`}>
                      {result.data.status}
                    </span>
                  </div>
                  {result.role === 'student' && (
                    <>
                      <div className="us-info-item">
                        <span className="us-info-label">Level</span>
                        <span className="us-info-value">{result.data.currentLevel}</span>
                      </div>
                      <div className="us-info-item">
                        <span className="us-info-label">Fee Status</span>
                        <span className={`us-badge ${result.data.feeStatus === 'Paid' ? 'us-badge-paid' : 'us-badge-pending'}`}>
                          {result.data.feeStatus}
                        </span>
                      </div>
                    </>
                  )}
                  {result.role === 'tutor' && (
                    <div className="us-info-item">
                      <span className="us-info-label">Specialization</span>
                      <span className="us-info-value">{getLangName(result.data.specialization) || 'N/A'}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Parent Info Card (Student Only) ── */}
              {result.role === 'student' && (
                <div className="us-card">
                  <div className="us-card-header">
                    <h3 className="us-card-title">{safeT('universalSearch.parentInfo', 'Parent Information')}</h3>
                  </div>
                  {result.parent ? (
                    <>
                      <div className="us-info-grid" style={{ marginBottom: '24px' }}>
                        <div className="us-info-item">
                          <span className="us-info-label">Name</span>
                          <span className="us-info-value">{getLangName(result.parent.name)}</span>
                        </div>
                        <div className="us-info-item">
                          <span className="us-info-label">Status</span>
                          <span className={`us-badge ${(result.parent.status === 'active' || result.parent.status === 'Active') ? 'us-badge-active' : 'us-badge-inactive'}`}>
                            {result.parent.status}
                          </span>
                        </div>
                        <div className="us-info-item">
                          <span className="us-info-label">Email</span>
                          <span className="us-info-value">{result.parent.email || 'N/A'}</span>
                        </div>
                        <div className="us-info-item">
                          <span className="us-info-label">Mobile</span>
                          <span className="us-info-value">{result.parent.mobile || 'N/A'}</span>
                        </div>
                      </div>
                      <div className="us-subcard">
                        <h4 className="us-subcard-title">{safeT('universalSearch.siblingsTitle', 'Other Linked Students')}</h4>
                        {result.siblings?.length > 0 ? (
                          <ul className="us-list">
                            {result.siblings.map(sib => (
                              <li className="us-list-item" key={sib._id}
                                onClick={() => handleSearch(null, sib.studentId)}
                                style={{ cursor: 'pointer', transition: 'background-color 0.2s', padding: '8px', borderRadius: '4px' }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--grey-pale)'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                                <span><strong>{getLangName(sib.name)}</strong> ({sib.studentId})</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span style={{ color: 'var(--mid-grey)', fontSize: '0.9rem' }}>{safeT('universalSearch.noSiblings', 'No other students linked.')}</span>
                        )}
                      </div>
                    </>
                  ) : (
                    <p style={{ color: 'var(--mid-grey)' }}>{safeT('universalSearch.noParent', 'No parent account linked.')}</p>
                  )}
                </div>
              )}

              {/* ── Linked Students Card (Parent Only) ── */}
              {result.role === 'parent' && (
                <div className="us-card">
                  <div className="us-card-header">
                    <h3 className="us-card-title">Linked Students</h3>
                  </div>
                  {result.siblings?.length > 0 ? (
                    <ul className="us-list">
                      {result.siblings.map(sib => (
                        <li className="us-list-item" key={sib._id}
                          onClick={() => handleSearch(null, sib.studentId)}
                          style={{ cursor: 'pointer', transition: 'background-color 0.2s', padding: '8px', borderRadius: '4px' }}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--grey-pale)'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                          <div>
                            <strong style={{ fontSize: '1.1rem', color: 'var(--wine)' }}>{getLangName(sib.name)}</strong>
                            <span style={{ marginLeft: '8px', color: 'var(--mid-grey)' }}>({sib.studentId})</span>
                            <div style={{ fontSize: '0.9rem', color: 'var(--dark-grey)', marginTop: '4px' }}>Level: {sib.currentLevel}</div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p style={{ color: 'var(--mid-grey)' }}>No students linked to this parent.</p>
                  )}
                </div>
              )}

              {/* ── Batches Card (Student & Tutor) ── */}
              {(result.role === 'student' || result.role === 'tutor') && (
                <div className="us-card">
                  <div className="us-card-header">
                    <h3 className="us-card-title">
                      <span style={{ marginRight: '8px' }}>{result.role === 'student' ? '🎓' : '👤'}</span>
                      {result.role === 'student'
                        ? safeT('universalSearch.enrolledBatches', 'Enrolled Batches')
                        : safeT('universalSearch.assignedBatches', 'Assigned Batches')}
                      <span className="us-count-badge">{result.batches?.length || 0}</span>
                    </h3>
                  </div>
                  {result.batches?.length > 0 ? (
                    <div className="us-batch-list">
                      {result.batches.map(b => (
                        <div key={b._id} className={`us-batch-row ${result.role === 'student' ? 'us-batch-row--student' : 'us-batch-row--tutor'}`}>
                          <div className="us-batch-row-info">
                            <div className="us-batch-row-name">{getLangName(b.name)}</div>
                            {(b.days?.length > 0 || b.time) && (
                              <div className="us-batch-row-schedule">
                                🕐 {b.days?.join(', ')} @ {b.time}
                              </div>
                            )}
                          </div>
                          {b.feeAmount && (
                            <span className="us-batch-fee">${b.feeAmount} HKD</span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--mid-grey)' }}>{safeT('universalSearch.noBatches', 'Not enrolled in any batches.')}</p>
                  )}
                </div>
              )}

              {/* ── Payment History Card (Student Only) ── */}
              {result.role === 'student' && (
                <div className="us-card" style={{ gridColumn: '1 / -1' }}>
                  <div className="us-card-header">
                    <h3 className="us-card-title">🧾 {safeT('universalSearch.paymentHistory', 'Payment History')}</h3>
                    <button onClick={handleDownloadPDF} className="us-btn-outline" style={{ borderColor: '#c62828', color: '#c62828', fontSize: '0.9rem', padding: '8px 16px' }}>
                      📄 Export PDF
                    </button>
                  </div>

                  {/* Outstanding Fees Section */}
                  {pendingMonths.length > 0 && (
                    <div className="us-outstanding-box">
                      <div className="us-outstanding-header">
                        <span>⚠️</span>
                        <strong>Outstanding Fees — {pendingMonths.length} month{pendingMonths.length === 1 ? '' : 's'} unpaid</strong>
                      </div>
                      {Object.entries(groupedPending).map(([batchName, months]) => (
                        <div key={batchName} style={{ marginTop: '12px' }}>
                          <div className="us-outstanding-batch-chip">{batchName}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                            {months.map((pm, i) => (
                              <div key={i} className="us-pending-row">
                                <span className="us-pending-month">{pm.month}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span className="us-badge us-badge-unpaid">Unpaid</span>
                                  {pm.expectedAmount && (
                                    <span className="us-pending-amount">${pm.expectedAmount} HKD</span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Paid / Pending Payments */}
                  {result.payments?.length > 0 ? (
                    <div style={{ marginTop: pendingMonths.length > 0 ? '24px' : '0' }}>
                      {Object.entries(groupedPayments).map(([batchName, payments], index) => (
                        <div key={index} style={{ marginBottom: index === Object.entries(groupedPayments).length - 1 ? 0 : '32px' }}>
                          <div className="us-payment-group-chip">{batchName}</div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                            {payments.map(p => {
                              const isApproved = p.verificationStatus === 'Approved';
                              const dateStr = p.paymentDate || p.createdAt
                                ? new Date(p.paymentDate || p.createdAt).toLocaleDateString()
                                : 'N/A';
                              return (
                                <div key={p._id} className={`us-payment-row ${isApproved ? 'us-payment-row--approved' : 'us-payment-row--pending'}`}>
                                  <div className="us-payment-row-top">
                                    <span className="us-payment-month">{p.month || 'N/A'}</span>
                                    <span className={`us-badge ${isApproved ? 'us-badge-paid' : 'us-badge-pending'}`}>
                                      {p.verificationStatus}
                                    </span>
                                  </div>
                                  <div className="us-payment-row-bottom">
                                    <span className="us-payment-meta">📅 {dateStr}</span>
                                    <span className="us-payment-meta">💳 {p.method || 'N/A'}</span>
                                    <span className="us-payment-amount">${p.amount || 0} HKD</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : pendingMonths.length === 0 ? (
                    <p style={{ color: 'var(--mid-grey)' }}>{safeT('universalSearch.noPayments', 'No payment records found.')}</p>
                  ) : null}
                </div>
              )}

            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
